import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  updateAutoApproveVerifiedModels,
  updateModelApprovalSetting,
  updateVerifiedModelNotifications,
} from '@/api/model-management';
import useWorkspacesStore from '@/state/workspaces.state';
import { useGlobalStore } from '@/state/global.state';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_CUSTOM_MODEL_APPROVAL } from '@/consts/featureFlags';
import { MODEL_SETTING_CHANGED_EVENT } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';

const logger = log.getLogger('useModelManagement');

export const useIsCustomModelApprovalEnabled = () => {
  const { value: ffEnabled } = useFeatureFlag(FF_CUSTOM_MODEL_APPROVAL);
  const activeWorkspace = useWorkspacesStore((state) => state.activeWorkspace);
  const isModelApprovalSettingEnabled = activeWorkspace.isModelApprovalSettingEnabled ?? false;
  const isModelBlockingEnabled = activeWorkspace.isModelBlockingEnabled ?? false;
  const shouldBlockUnverifiedModels = activeWorkspace.preferences?.shouldBlockUnverifiedModels ?? false;
  return ffEnabled && isModelApprovalSettingEnabled && isModelBlockingEnabled && !shouldBlockUnverifiedModels;
};

export const useUpdateModelApprovalSetting = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const activeWorkspace = useWorkspacesStore((state) => state.activeWorkspace);
  const updateActiveWorkspace = useWorkspacesStore((state) => state.updateActiveWorkspace);
  const { updateSnackbarData } = useGlobalStore();

  return useMutation({
    mutationFn: async (checked: boolean) => {
      const workspaceId = activeWorkspace.workspaceId;
      await updateModelApprovalSetting(workspaceId, checked);
      return workspaceId;
    },
    onSuccess: (workspaceId, enabled) => {
      track(MODEL_SETTING_CHANGED_EVENT, { workspaceId, setting: 'importing', enabled }, TrackTypeEnum.Product);
    },
    onMutate: (checked: boolean) => {
      const previousValue = activeWorkspace.isModelApprovalSettingEnabled ?? false;
      updateActiveWorkspace({ isModelApprovalSettingEnabled: checked });
      return { previousValue };
    },
    onError: (_err, _checked, context) => {
      logger.error('Error updating model importing enabled setting', _err);
      updateActiveWorkspace({ isModelApprovalSettingEnabled: context?.previousValue });
      updateSnackbarData({
        text: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.ENABLE_IMPORTING.ERROR),
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    },
  });
};

export const useUpdateAutoApprove = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const activeWorkspace = useWorkspacesStore((state) => state.activeWorkspace);
  const updateActiveWorkspace = useWorkspacesStore((state) => state.updateActiveWorkspace);
  const { updateSnackbarData } = useGlobalStore();

  return useMutation({
    mutationFn: async (checked: boolean) => {
      const workspaceId = activeWorkspace.workspaceId;
      await updateAutoApproveVerifiedModels(workspaceId, checked);
      return workspaceId;
    },
    onSuccess: (workspaceId, enabled) => {
      track(MODEL_SETTING_CHANGED_EVENT, { workspaceId, setting: 'auto_approve', enabled }, TrackTypeEnum.Product);
    },
    onMutate: (checked: boolean) => {
      const workspace = useWorkspacesStore.getState().activeWorkspace;
      const previousValue = workspace.preferences?.shouldAutoApproveVerifiedModels;
      updateActiveWorkspace({
        preferences: { ...workspace.preferences, shouldAutoApproveVerifiedModels: checked },
      });
      return { previousValue, workspaceId: workspace.workspaceId };
    },
    onError: (_err, _checked, context) => {
      logger.error('Error updating auto-approve verified models preference', _err);
      const workspace = useWorkspacesStore.getState().activeWorkspace;
      if (workspace.workspaceId === context?.workspaceId) {
        updateActiveWorkspace({
          preferences: { ...workspace.preferences, shouldAutoApproveVerifiedModels: context.previousValue },
        });
      }
      updateSnackbarData({
        text: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.AUTO_APPROVE_VERIFIED.ERROR),
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    },
  });
};

export const useUpdateVerifiedModelNotifications = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const activeWorkspace = useWorkspacesStore((state) => state.activeWorkspace);
  const updateActiveWorkspace = useWorkspacesStore((state) => state.updateActiveWorkspace);
  const { updateSnackbarData } = useGlobalStore();

  return useMutation({
    mutationFn: async (checked: boolean) => {
      const workspaceId = activeWorkspace.workspaceId;
      await updateVerifiedModelNotifications(workspaceId, checked);
      return workspaceId;
    },
    onSuccess: (workspaceId, enabled) => {
      track(MODEL_SETTING_CHANGED_EVENT, { workspaceId, setting: 'notifications', enabled }, TrackTypeEnum.Product);
    },
    onMutate: (checked: boolean) => {
      const workspace = useWorkspacesStore.getState().activeWorkspace;
      const previousValue = workspace.preferences?.shouldNotifyVerifiedModels;
      updateActiveWorkspace({
        preferences: { ...workspace.preferences, shouldNotifyVerifiedModels: checked },
      });
      return { previousValue, workspaceId: workspace.workspaceId };
    },
    onError: (_err, _checked, context) => {
      logger.error('Error updating verified model notifications preference', _err);
      const workspace = useWorkspacesStore.getState().activeWorkspace;
      if (workspace.workspaceId === context?.workspaceId) {
        updateActiveWorkspace({
          preferences: { ...workspace.preferences, shouldNotifyVerifiedModels: context.previousValue },
        });
      }
      updateSnackbarData({
        text: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.NOTIFY_VERIFIED.ERROR),
        isOpen: true,
        icon: null,
        severity: 'error',
      });
    },
  });
};
